import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { AppTheme } from '@/theme/theme';
import { useTheme } from '@/theme';
import { MenuItem, type MenuItemVariation } from '../MenuItem';
import { useSettingsStore } from '@/store/settingsStore';
import { previewAndUnlockAlarmSound } from '@/utils/alarmSound';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useAuthStore } from '@/store/authStore';
import { InquiryModal } from '@/components/modals/InquiryModal';

interface SettingsProps {
  isOpen: boolean;
  theme: AppTheme;
}

const DropdownWrapper = styled.div`
  position: relative;
`;

const Dropdown = styled.div<{ theme: AppTheme }>`
  position: absolute;
  bottom: 100%;
  left: ${({ theme }) => theme.spacing[4]}px;
  background: ${({ theme }) => theme.colors.surface.layout.overlay};
  border-radius: 8px;
  padding: 16px;
  min-width: 200px;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.border.shadow.md};
  z-index: 1000;
`;

const SettingItem = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.div<{ theme: AppTheme }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LanguageOption = styled.div<{ theme: AppTheme; isSelected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.surface.interactive.selectedNav : 'transparent'};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background 0.2s;
  font-size: 14px;

  &:hover {
    background: ${({ theme }) => theme.colors.surface.interactive.hover};
  }
`;

const DarkModeRow = styled.div<{ theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface.interactive.hover};
  }
`;

const DarkModeLabel = styled.span<{ theme: AppTheme }>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToggleSwitch = styled.div<{ isOn: boolean }>`
  width: 44px;
  height: 24px;
  background: ${({ theme, isOn }) =>
    isOn ? (theme as AppTheme).colors.control.toggle.on : (theme as AppTheme).colors.control.toggle.off};
  border-radius: 12px;
  position: relative;
  transition: background 0.3s;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => (theme as AppTheme).colors.control.toggle.thumb};
    border-radius: 50%;
    top: 2px;
    left: ${({ isOn }) => (isOn ? '22px' : '2px')};
    transition: left 0.3s;
  }
`;

export const Settings: React.FC<SettingsProps> = ({ isOpen, theme }) => {
  const variation: MenuItemVariation = 'settings';
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const { themeMode, toggleTheme } = useTheme();
  const alarmSoundEnabled = useSettingsStore(state => state.alarmSoundEnabled);
  const setAlarmSoundEnabled = useSettingsStore(state => state.setAlarmSoundEnabled);
  const user = useAuthStore(state => state.user);

  // 외부 클릭 감지
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSettingsClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang).then(() => {
      window.dispatchEvent(new Event('languageChanged'));
    });
  };

  const handleToggleAlarmSound = () => {
    const next = !alarmSoundEnabled;
    setAlarmSoundEnabled(next);
    // 켤 때는 클릭(사용자 제스처) 중에 1회 재생하여 autoplay 차단을 해제
    if (next) {
      previewAndUnlockAlarmSound();
    }
  };

  const handleOpenInquiry = () => {
    setIsDropdownOpen(false);
    setIsInquiryOpen(true);
  };

  return (
    <DropdownWrapper ref={dropdownRef}>
      <MenuItem
        icon="/icons/IconSettings.svg"
        label="Settings"
        isOpen={isOpen}
        theme={theme}
        variation={variation}
        onClick={handleSettingsClick}
      />

      {isDropdownOpen && (
        <Dropdown theme={theme}>
          <SettingItem>
            <SettingLabel theme={theme}>Language</SettingLabel>
            <LanguageOption
              theme={theme}
              isSelected={i18n.language === 'en'}
              onClick={() => handleLanguageChange('en')}
            >
              English
            </LanguageOption>
            <LanguageOption
              theme={theme}
              isSelected={i18n.language === 'ko'}
              onClick={() => handleLanguageChange('ko')}
            >
              한국어
            </LanguageOption>
            <LanguageOption
              theme={theme}
              isSelected={i18n.language === 'es'}
              onClick={() => handleLanguageChange('es')}
            >
              Español
            </LanguageOption>
          </SettingItem>

          <SettingItem>
            <SettingLabel theme={theme}>Theme</SettingLabel>
            <DarkModeRow theme={theme} onClick={toggleTheme}>
              <DarkModeLabel theme={theme}>Dark Mode</DarkModeLabel>
              <ToggleSwitch isOn={themeMode === 'dark'} />
            </DarkModeRow>
          </SettingItem>

          <SettingItem>
            <SettingLabel theme={theme}>Sound</SettingLabel>
            <DarkModeRow theme={theme} onClick={handleToggleAlarmSound}>
              <DarkModeLabel theme={theme}>Alarm Sound</DarkModeLabel>
              <ToggleSwitch isOn={alarmSoundEnabled} />
            </DarkModeRow>
          </SettingItem>

          <SettingItem>
            <SettingLabel theme={theme}>Support</SettingLabel>
            <LanguageOption theme={theme} isSelected={false} onClick={handleOpenInquiry}>
              {t(i18nKeys.common.inquiry_menu)}
            </LanguageOption>
          </SettingItem>
        </Dropdown>
      )}

      <InquiryModal
        isOpen={isInquiryOpen}
        onClose={() => setIsInquiryOpen(false)}
        source="settings"
        prefill={{
          name: user?.name,
          email: user?.email,
          roleName: user?.role_name,
        }}
      />
    </DropdownWrapper>
  );
};
